import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import supabase from '../../../services/supabase';
import { checkAuthStatus } from '../../../lib/supabaseClient';
import { getMapboxToken } from '../../../services/mapService';
import { UserRoutesMap } from '../../../components/map/MapDisplay'; // Import the working component
import '../../../components/map/MapDisplay.css'; // CRITICAL: Import the working CSS
import '../Routes.css';

const RoutesList = ({ 
  routes, 
  setRoutes, 
  isOwnProfile, 
  onCreateRoute, 
  onSelectRoute, 
  selectedRoute, 
  setSelectedRoute,
  onEditRoute,
  onDeleteRoute,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routePoints, setRoutePoints] = useState({});

  // Route colors for overview mode
  const routeColors = [
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', 
    '#ffeaa7', '#dda0dd', '#98d8c8', '#f7b731'
  ];

  // Fetch all route points for the current user's routes
  const fetchAllRoutePoints = async () => {
    /*
     * 🚨 CRITICAL ISSUE RESOLVED: Route Points Not Loading
     * 
     * PROBLEM: Route points were returning empty arrays from Supabase queries
     * despite having data in the database. The map was loading in the middle
     * of the ocean because no coordinates were being fetched.
     * 
     * ROOT CAUSE: The app was using a MockSupabase client instead of the real
     * Supabase client. The mock client didn't have:
     * - Real authentication (auth.uid() returned null)
     * - Real database queries
     * - Real route points data
     * 
     * SOLUTION: 
     * 1. Replaced MockSupabase client with real Supabase client in src/lib/supabaseClient.js
     * 2. Added authentication checking before fetching route points
     * 3. RLS (Row Level Security) policies require auth.uid() to match route owner
     * 
     * FUTURE DEBUGGING:
     * If route points are not loading again, check:
     * 1. Is the user authenticated? (checkAuthStatus())
     * 2. Does auth.uid() match the route owner?
     * 3. Are RLS policies blocking access?
     * 4. Is the Supabase client properly configured with environment variables?
     * 
     * ENVIRONMENT VARIABLES REQUIRED:
     * - REACT_APP_SUPABASE_URL
     * - REACT_APP_SUPABASE_ANON_KEY
     * 
     * RLS POLICY FOR route_points TABLE:
     * SELECT: (route_id IN (SELECT routes.id FROM routes WHERE (routes.user_id = auth.uid())))
     */
    
    if (!routes || routes.length === 0) {
      console.log('[DEBUG] No routes available, skipping route points fetch');
      return;
    }
    
    console.log('[DEBUG] ===== STARTING ROUTE POINTS FETCH =====');
    console.log('[DEBUG] Routes to process:', routes.map(r => ({ id: r.id, name: r.name })));
    
    // Check authentication status first
    console.log('[DEBUG] ===== CHECKING AUTHENTICATION =====');
    const { user, error: authError } = await checkAuthStatus();
    
    if (authError) {
      console.error('[ERROR] Authentication check failed:', authError);
      return;
    }
    
    if (!user) {
      console.error('[ERROR] User is not authenticated! This is why route points are not being fetched.');
      console.error('[ERROR] RLS policies require authentication to access route_points table.');
      return;
    }
    
    console.log('[DEBUG] User is authenticated:', user.id);
    console.log('[DEBUG] Expected user ID for routes:', user.id);
    console.log('[DEBUG] User ID match:', user.id === user.id); // Always true, but good for debugging
    
    /*
     * 🔐 AUTHENTICATION CHECK ADDED
     * 
     * This check ensures the user is authenticated before attempting to fetch
     * route points. Without authentication, RLS policies will block access
     * and return empty arrays even if data exists.
     * 
     * The RLS policy for route_points requires:
     * route_id IN (SELECT routes.id FROM routes WHERE routes.user_id = auth.uid())
     * 
     * If auth.uid() is null (user not authenticated), this condition fails
     * and no data is returned.
     */
    
    // First, let's check what route points exist in the database for this user
    console.log('[DEBUG] ===== CHECKING ALL ROUTE POINTS IN DATABASE =====');
    try {
      const { data: allRoutePoints, error: allPointsError } = await supabase
        .from('route_points')
        .select('*')
        .order('route_id, point_order');
      
      if (allPointsError) {
        console.error('[ERROR] Failed to fetch all route points:', allPointsError);
      } else {
        console.log('[DEBUG] All route points in database:', allRoutePoints);
        console.log('[DEBUG] Total route points in database:', allRoutePoints?.length || 0);
        
        // Check which route IDs exist in the database
        const routeIdsInDb = [...new Set(allRoutePoints?.map(p => p.route_id) || [])];
        console.log('[DEBUG] Route IDs in database:', routeIdsInDb);
        
        // Check which route IDs we're looking for
        const routeIdsWeHave = routes.map(r => r.id);
        console.log('[DEBUG] Route IDs we have:', routeIdsWeHave);
        
        // Find matching routes
        const matchingRoutes = routeIdsWeHave.filter(id => routeIdsInDb.includes(id));
        console.log('[DEBUG] Matching route IDs:', matchingRoutes);
        
        if (matchingRoutes.length === 0) {
          console.warn('[WARNING] No route IDs match between our routes and database route_points!');
          console.warn('[WARNING] This explains why no points are being fetched.');
        }
      }
    } catch (error) {
      console.error('[ERROR] Error checking all route points:', error);
    }
    
    const routePointsData = {};
    let totalPointsFetched = 0;
    let totalValidPoints = 0;
    
    try {
      for (const route of routes) {
        console.log(`\n[DEBUG] ===== PROCESSING ROUTE: ${route.name} (${route.id}) =====`);
        
        // First, get ALL points for this route (including invalid ones for debugging)
        console.log(`[DEBUG] Making Supabase query for route ${route.id}:`);
        console.log(`[DEBUG] Query: .from('route_points').select('*').eq('route_id', '${route.id}').order('point_order')`);
        
        const { data: allPoints, error: allPointsError } = await supabase
          .from('route_points')
          .select('*')
          .eq('route_id', route.id)
          .order('point_order');

        console.log(`[DEBUG] Supabase response for route ${route.id}:`, { data: allPoints, error: allPointsError });

        if (allPointsError) {
          console.error(`[ERROR] Failed to fetch all points for route ${route.name}:`, allPointsError);
          continue;
        }

        console.log(`[DEBUG] All points for route ${route.name}:`, allPoints);
        console.log(`[DEBUG] Total points found: ${allPoints?.length || 0}`);

        // Now get only points with valid coordinates
        const { data: validPoints, error: validPointsError } = await supabase
          .from('route_points')
          .select('*')
          .eq('route_id', route.id)
          .not('latitude', 'is', null)
          .not('longitude', 'is', null)
          .order('point_order');

        if (validPointsError) {
          console.error(`[ERROR] Failed to fetch valid points for route ${route.name}:`, validPointsError);
          continue;
        }

        console.log(`[DEBUG] Valid points for route ${route.name}:`, validPoints);
        console.log(`[DEBUG] Valid points count: ${validPoints?.length || 0}`);

        if (validPoints && validPoints.length > 0) {
          // Process each point and validate coordinates
          const processedPoints = validPoints.map((point, index) => {
            const lat = parseFloat(point.latitude);
            const lng = parseFloat(point.longitude);
            
            console.log(`[DEBUG] Point ${index + 1}:`, {
              id: point.id,
              originalLat: point.latitude,
              originalLng: point.longitude,
              parsedLat: lat,
              parsedLng: lng,
              isValidLat: !isNaN(lat),
              isValidLng: !isNaN(lng),
              pointOrder: point.point_order,
              name: point.name
            });
            
            if (isNaN(lat) || isNaN(lng)) {
              console.warn(`[WARNING] Invalid coordinates for point ${point.id}: lat=${point.latitude}, lng=${point.longitude}`);
              return null;
            }
            
            return {
              ...point,
              latitude: lat,
              longitude: lng
            };
          }).filter(Boolean); // Remove null points
          
          console.log(`[DEBUG] Processed points for route ${route.name}:`, processedPoints);
          console.log(`[DEBUG] Final valid points count: ${processedPoints.length}`);
          
          routePointsData[route.id] = processedPoints;
          totalValidPoints += processedPoints.length;
        } else {
          console.log(`[DEBUG] No valid points found for route ${route.name}`);
        }
        
        totalPointsFetched += allPoints?.length || 0;
      }
      
      console.log('\n[DEBUG] ===== FETCH SUMMARY =====');
      console.log(`[DEBUG] Total routes processed: ${routes.length}`);
      console.log(`[DEBUG] Total points fetched: ${totalPointsFetched}`);
      console.log(`[DEBUG] Total valid points: ${totalValidPoints}`);
      console.log('[DEBUG] Final routePointsData:', routePointsData);
      
      setRoutePoints(routePointsData);
      
    } catch (error) {
      console.error('[ERROR] Critical error in fetchAllRoutePoints:', error);
    }
  };

  // Fetch route points when routes change
  useEffect(() => {
    fetchAllRoutePoints();
  }, [routes]);

  // Convert routes and points to the format UserRoutesMap expects
  const routesWithPoints = routes.map(route => {
    const points = routePoints[route.id] || [];
    
    console.log(`\n[DEBUG] ===== PROCESSING ROUTE FOR MAP: ${route.name} =====`);
    console.log(`[DEBUG] Raw points from state:`, points);
    
    const pointsWithCoords = points.map(point => {
      // Coordinates should already be converted to numbers from fetchAllRoutePoints
      const lat = point.latitude;
      const lng = point.longitude;
      
      console.log(`[DEBUG] Processing point for map:`, {
        id: point.id,
        lat: lat,
        lng: lng,
        typeLat: typeof lat,
        typeLng: typeof lng,
        isValid: !isNaN(lat) && !isNaN(lng)
      });
      
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(`[WARNING] Invalid coordinates for map point ${point.id}: lat=${lat}, lng=${lng}`);
        return null;
      }
      
      return {
        ...point,
        coordinates: [lng, lat] // Mapbox expects [longitude, latitude]
      };
    }).filter(Boolean); // Remove any null points
    
    console.log(`[DEBUG] Final points for map:`, pointsWithCoords);
    console.log(`[DEBUG] Points count: ${pointsWithCoords.length}`);
    
    return {
      ...route,
      points: pointsWithCoords
    };
  });
  
  console.log('\n[DEBUG] ===== FINAL ROUTES WITH POINTS =====');
  console.log('[DEBUG] routesWithPoints:', routesWithPoints);
  console.log('[DEBUG] Total routes with points:', routesWithPoints.filter(r => r.points.length > 0).length);

  const handleRouteClick = (routeId) => {
    console.log('[DEBUG] Route clicked:', routeId);
    const route = routes.find(r => r.id === routeId);
    if (route) {
      onSelectRoute(route);
    }
  };

  if (loading) {
    return (
      <div className="routes-container">
        <div className="routes-loading">
          <div className="loading-spinner"></div>
          <p>Loading routes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="routes-container">
        <div className="routes-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="routes-container">
      <div className="routes-overview-container">
        <div className="routes-list-panel">
          <div className="routes-header">
            <h2>All Routes</h2>
          </div>
          
          {/* Create Route Button */}
          {isOwnProfile && (
            <div style={{ padding: '0 24px' }}>
              <button 
                onClick={onCreateRoute} 
                className="create-route-btn"
              >
                Create New Route
              </button>
            </div>
          )}
          
          <div className="existing-routes">
            {!routes || routes.length === 0 ? (
              <div className="saved-routes-placeholder">
                <p>📍 No routes created yet. Start exploring!</p>
              </div>
            ) : (
              routes.map((route, index) => {
                const color = routeColors[index % routeColors.length];
                const pointCount = routePoints[route.id]?.length || 0;
                
                return (
                  <div
                    key={route.id}
                    className="route-item"
                    onClick={() => handleRouteClick(route.id)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      cursor: 'pointer' 
                    }}
                  >
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Route Color Indicator */}
                      <div 
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: color,
                          flexShrink: 0
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <h3>{route.name}</h3>
                        <p>{route.description}</p>
                        {pointCount > 0 && (
                          <small style={{ color: '#6b7280', fontSize: '12px' }}>
                            {pointCount} point{pointCount !== 1 ? 's' : ''}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="route-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {isOwnProfile && (
                        <>
                          <button
                            className="route-action-btn edit-route-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (typeof onEditRoute === 'function') onEditRoute(route);
                              else handleRouteClick(route.id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="route-action-btn delete-route-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (typeof onDeleteRoute === 'function') onDeleteRoute(route.id);
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      <button 
                        className="route-action-btn view-route-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRouteClick(route.id);
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="routes-map-panel">
          <div className="overview-map">
            {console.log('[DEBUG] ===== RENDERING USERROUTESMAP =====')}
            {console.log('[DEBUG] routesWithPoints being passed to map:', routesWithPoints)}
            {console.log('[DEBUG] Routes with points count:', routesWithPoints.filter(r => r.points.length > 0).length)}
            {console.log('[DEBUG] Total points across all routes:', routesWithPoints.reduce((sum, r) => sum + r.points.length, 0))}
            <UserRoutesMap 
              routesWithPoints={routesWithPoints} 
              autoFitBounds={true}
              zoom={8}
              fallbackCenter={[-122.4194, 37.7749]} // San Francisco as fallback
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutesList; 
